/**
 * NewCaseDetectionDialog - Prompts user when new-case detection rules match.
 * Used during enrollment creation when PROMPT_USER action is returned.
 */

import React, { useState } from 'react';
import { AlertTriangle, FileText, Plus, X } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { NewCaseDetectResult, caseEpisodesAPI, EpisodeDetectionResult } from '@/services/api/caseEpisodes';

interface NewCaseDetectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  detection: NewCaseDetectResult | EpisodeDetectionResult;
  memberId: number;
  programId: number;
  cboId: number;
  onCaseCreated?: (caseNumber: string, episodeId: number) => void;
  onSkipped?: () => void;
}

export default function NewCaseDetectionDialog({
  open, onOpenChange, detection, memberId, programId, cboId,
  onCaseCreated, onSkipped,
}: NewCaseDetectionDialogProps) {
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const result = await caseEpisodesAPI.createEpisode({
        cbo_id: cboId,
        member_id: memberId,
        program_id: programId,
      });
      onCaseCreated?.(result.case_number, result.id);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create new case episode:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleSkip = () => {
    onSkipped?.();
    onOpenChange(false);
  };

  const isPrompt = detection.action === 'PROMPT_USER';
  const isFlagReview = detection.action === 'FLAG_FOR_REVIEW';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isPrompt ? 'bg-blue-100' : 'bg-yellow-100'}`}>
              {isPrompt ? (
                <FileText className="w-5 h-5 text-blue-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              )}
            </div>
            <div>
              <DialogTitle>
                {isPrompt ? 'New Case Detected' : 'Case Review Needed'}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            {detection.reason}
          </p>

          {'matched_rule_name' in detection && detection.matched_rule_name && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Detection rule:</span>
              <Badge variant="outline" className="text-xs">
                {detection.matched_rule_name}
              </Badge>
            </div>
          )}

          {detection.auto_archive_previous && detection.existing_episode_id && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
              <p className="text-xs text-amber-800 dark:text-amber-200">
                The current active episode will be automatically archived when the new case is created.
              </p>
            </div>
          )}

          {isPrompt && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Would you like to create a new case episode for this member?
            </p>
          )}

          {isFlagReview && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              This case has been flagged for review. You can create a new episode now or skip and review later.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={handleSkip}>
            <X className="w-4 h-4 mr-1" />
            {isPrompt ? 'Skip' : 'Review Later'}
          </Button>
          <Button onClick={handleCreate} disabled={creating}>
            <Plus className="w-4 h-4 mr-1" />
            {creating ? 'Creating...' : 'Create New Case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
